import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  ArrowLeft, User, CreditCard, Settings2, LogOut,
  Mail, Globe, Moon, Sun, Trash2, Check, Crown, Zap, BarChart3, Volume2
} from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '@/components/AppShell';
import { LANGUAGES } from '@/components/LanguageSwitcher';
import { useDarkMode } from '@/contexts/DarkModeContext';
import DarkModeToggle from '@/components/DarkModeToggle';
import { useUser } from '@/contexts/UserContext';
import { DEFAULT_TTS_VOICE, SETTINGS_VOICE_OPTIONS } from '@/components/VoiceControls';
import { StoryPageHeader, StorySurface } from '@/components/story/StoryPrimitives';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS = {
  free: { price: 0, features: ['1 book, up to 3 chapters', '3 AI polish & ignite uses', '1 interview session to try it', 'No exports'] },
  premium: { price: 12, features: ['3 books, unlimited chapters', 'Unlimited interview sessions', 'AI polish & ignite (30/mo)', 'PDF, EPUB & Word exports', 'Text-to-speech (15/mo)'] },
  pro: { price: 19, features: ['Unlimited books', 'Everything in Writer', 'Unlimited AI — polish, ignite & TTS', 'All export formats incl. print-ready', 'Priority support'] }
};

function SettingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { darkMode } = useDarkMode();
  const { isAdmin } = useUser();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [subscription, setSubscription] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  
  // Preferences state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(() => {
    const savedVoice = localStorage.getItem('storyteller_voice');
    return SETTINGS_VOICE_OPTIONS[savedVoice] ? savedVoice : DEFAULT_TTS_VOICE;
  });
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const lang = i18n.language || 'en';
    return LANGUAGES.find(l => l.code === lang)?.code
      || LANGUAGES.find(l => lang.startsWith(l.code))?.code
      || 'en';
  });

  const loadUserData = useCallback(async () => {
    try {
      const [userRes, subRes, emailPrefsRes] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/subscription`).catch(() => ({ data: null })),
        axios.get(`${API}/email/preferences`).catch(() => ({ data: null }))
      ]);
      setUser(userRes.data);
      if (subRes.data) {
        setSubscription(subRes.data);
        setBillingHistory(subRes.data.billing_history || []);
      }
      if (emailPrefsRes.data) {
        const prefs = emailPrefsRes.data;
        const generalEmails = Boolean(
          prefs.writing_tips || prefs.product_updates || prefs.weekly_digest || prefs.marketing
        );
        setEmailNotifications(generalEmails);
        setWeeklyDigest(Boolean(prefs.weekly_digest));
        setMarketingEmails(Boolean(prefs.marketing));
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out');
      return;
    }

    setProcessingPayment(true);
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success(t('settings.paymentSuccess'));
        setProcessingPayment(false);
        loadUserData();
        navigate('/settings', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
        setProcessingPayment(false);
        navigate('/settings', { replace: true });
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setProcessingPayment(false);
    }
  }, [t, loadUserData, navigate]);

  useEffect(() => {
    loadUserData();
    
    // Check for payment status from URL
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPaymentStatus(sessionId);

    // Auto-open subscription tab when redirected from upgrade prompt
    const tab = searchParams.get('tab');
    if (tab === 'subscription') setActiveTab('subscription');
  }, [searchParams, loadUserData, pollPaymentStatus]);

  const handleUpgrade = async (plan) => {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/checkout`,
        { plan, origin_url: originUrl },
        
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast.error('Failed to start checkout');
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setApplyingCoupon(true);
    try {
      const token = localStorage.getItem('auth_token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.post(
        `${API}/coupons/apply`,
        { code: couponCode.trim() },
        config
      );
      
      if (response.data.success) {
        toast.success(response.data.message || 'Coupon applied successfully!');
        setCouponCode('');
        // Reload user data to show updated tier
        loadUserData();
      } else {
        toast.error(response.data.message || 'Failed to apply coupon');
      }
    } catch (error) {
      console.error('Failed to apply coupon:', error);
      const errorMessage = error.response?.data?.detail || 'Invalid coupon code';
      toast.error(errorMessage);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/portal`, { origin_url: originUrl });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      toast.error('Failed to open billing portal');
    }
  };

  const handleVoiceChange = (voiceId) => {
    setSelectedVoice(voiceId);
    localStorage.setItem('storyteller_voice', voiceId);
    window.dispatchEvent(new Event('storyteller-voice-change'));
    toast.success(`Read aloud voice set to ${SETTINGS_VOICE_OPTIONS[voiceId].name}`);
  };

  const handleLanguageChange = (langCode) => {
    const language = LANGUAGES.find(l => l.code === langCode);
    i18n.changeLanguage(langCode);
    document.documentElement.setAttribute('dir', language?.dir || 'ltr');
    document.documentElement.setAttribute('lang', langCode);
    setSelectedLanguage(langCode);
    toast.success(`Language set to ${language?.name || langCode}`);
  };

  const updateEmailPreferences = useCallback(async (partial) => {
    const next = {
      weekly_digest: partial.weekly_digest ?? weeklyDigest,
      writing_tips: partial.writing_tips ?? emailNotifications,
      product_updates: partial.product_updates ?? emailNotifications,
      marketing: partial.marketing ?? marketingEmails,
    };

    setEmailNotifications(Boolean(
      next.writing_tips || next.product_updates || next.weekly_digest || next.marketing
    ));
    setWeeklyDigest(Boolean(next.weekly_digest));
    setMarketingEmails(Boolean(next.marketing));

    try {
      await axios.put(`${API}/email/preferences`, next);
    } catch (error) {
      console.error('Failed to update email preferences:', error);
      toast.error('Could not update email preferences');
    }
  }, [emailNotifications, marketingEmails, weeklyDigest]);

  const handleEmailNotificationsChange = (checked) => {
    updateEmailPreferences({
      weekly_digest: checked ? weeklyDigest : false,
      writing_tips: checked,
      product_updates: checked,
      marketing: checked ? marketingEmails : false,
    });
  };

  const handleWeeklyDigestChange = (checked) => {
    updateEmailPreferences({
      weekly_digest: checked,
      writing_tips: checked ? true : emailNotifications,
      product_updates: emailNotifications,
    });
  };

  const handleMarketingEmailsChange = (checked) => {
    updateEmailPreferences({
      marketing: checked,
      writing_tips: checked ? true : emailNotifications,
      product_updates: emailNotifications,
    });
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
  };

  const currentPlan = subscription?.plan || 'free';
  const settingsTabClass = (tab) =>
    `story-tab ${activeTab === tab ? 'story-tab-active' : ''} flex-shrink-0 lg:w-full justify-start`;

  if (loading) {
    return (
      <div className="story-page flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--story-primary)]/20 border-t-[var(--story-primary)]"></div>
      </div>
    );
  }

  const contextualItems = [
    { icon: ArrowLeft, label: 'Back to Books', onClick: () => navigate('/dashboard'), testId: 'back-to-dashboard-button' },
    { divider: true },
    { icon: LogOut, label: 'Sign Out', onClick: handleLogout, variant: 'destructive', testId: 'logout-button' },
  ];

  return (
    <AppShell contextualItems={contextualItems} backTo={{ label: 'Books', onClick: () => navigate('/dashboard') }} className="mobile-page">
      <main className="story-container max-w-7xl pt-14 pb-4 sm:pt-12 sm:pb-8">
        <StoryPageHeader
          eyebrow="Settings"
          title="Your writing workspace"
          description="Account, plan, voice, and reading preferences live here without turning the app into an admin panel."
        />
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Sidebar Navigation - Horizontal on mobile */}
          <div className="lg:col-span-1">
            <StorySurface subtle className="p-2 flex lg:flex-col gap-2 overflow-x-auto">
              <button
                onClick={() => setActiveTab('profile')}
                data-testid="settings-tab-profile"
                className={settingsTabClass('profile')}
              >
                <User className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-medium text-sm sm:text-base">{t('settings.profile')}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('subscription')}
                data-testid="settings-tab-subscription"
                className={settingsTabClass('subscription')}
              >
                <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-medium text-sm sm:text-base">{t('settings.subscription')}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('preferences')}
                data-testid="settings-tab-preferences"
                className={settingsTabClass('preferences')}
              >
                <Settings2 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-medium text-sm sm:text-base">{t('settings.preferences')}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('account')}
                data-testid="settings-tab-account"
                className={settingsTabClass('account')}
              >
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-medium text-sm sm:text-base">{t('settings.account')}</span>
              </button>
              
              {/* Admin Dashboard Link — only for admins */}
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  data-testid="settings-admin-link"
                  className="story-tab flex-shrink-0 lg:w-full justify-start border border-[var(--story-line)] lg:mt-2"
                >
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium text-sm sm:text-base">Admin</span>
                </button>
              )}
            </StorySurface>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-4 sm:space-y-6">
            {/* Processing Payment Banner */}
            {processingPayment && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="text-sm sm:text-base text-blue-800 dark:text-blue-200">{t('settings.processingPayment')}</span>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="bg-white/60 dark:bg-[#22201C]/70 backdrop-blur-sm border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-4 sm:mb-6">{t('settings.profile')}</h2>
                
                <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 mb-6 sm:mb-8">
                  <img 
                    src={user?.picture || 'https://via.placeholder.com/100'} 
                    alt={user?.name}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-[#E0D8CC] dark:border-[#2A2520]"
                  />
                  <div className="text-center sm:text-left">
                    <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4]">{user?.name}</h3>
                    <p className="text-[#2A2420]/70 dark:text-[#F0EBE4]/70">{user?.email}</p>
                    <div className="mt-2 flex items-center space-x-2">
                      {currentPlan === 'premium' && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                          <Crown className="w-4 h-4 mr-1" />
                          Premium
                        </span>
                      )}
                      {currentPlan === 'pro' && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-yellow-500 to-[#8B7355] text-white">
                          <Zap className="w-4 h-4 mr-1" />
                          Pro
                        </span>
                      )}
                      {currentPlan === 'free' && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-200 dark:bg-[#2A2520] text-gray-700 dark:text-gray-300">
                          {t('settings.free')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-[#E0D8CC] dark:border-[#2A2520]/50">
                    <span className="text-[#2A2420] dark:text-[#F0EBE4] font-medium">{t('settings.name')}</span>
                    <span className="text-[#2A2420] dark:text-[#F0EBE4]">{user?.name}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#E0D8CC] dark:border-[#2A2520]/50">
                    <span className="text-[#2A2420] dark:text-[#F0EBE4] font-medium">{t('settings.email')}</span>
                    <span className="text-[#2A2420] dark:text-[#F0EBE4]">{user?.email}</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-[#2A2420] dark:text-[#F0EBE4] font-medium">{t('settings.memberSince')}</span>
                    <span className="text-[#2A2420] dark:text-[#F0EBE4]">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Subscription Tab */}
            {activeTab === 'subscription' && (
              <div className="space-y-6">
                <div className="bg-white/60 dark:bg-[#22201C]/70 backdrop-blur-sm border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-2xl p-6">
                  <h2 className="text-xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-6">{t('settings.currentPlan')}</h2>

                  {/* Subscription status banners */}
                  {subscription?.stripe_status === 'past_due' && (
                    <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl flex items-start justify-between gap-4">
                      <div>
                        <p className="font-semibold text-red-800 dark:text-red-300">Payment failed</p>
                        <p className="text-sm text-red-700 dark:text-red-400 mt-1">Your last payment didn't go through. Update your payment method to keep your subscription.</p>
                      </div>
                      <Button onClick={handleManageBilling} className="shrink-0 bg-red-600 hover:bg-red-700 text-white text-sm">Fix payment</Button>
                    </div>
                  )}
                  {subscription?.cancel_at_period_end && subscription?.current_period_end && (
                    <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-xl">
                      <p className="font-semibold text-yellow-800 dark:text-yellow-300">Cancellation scheduled</p>
                      <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                        Your plan will downgrade to Free on {new Date(subscription.current_period_end).toLocaleDateString()}.
                      </p>
                    </div>
                  )}
                  {currentPlan !== 'free' && subscription?.current_period_end && !subscription?.cancel_at_period_end && (
                    <div className="mb-4 p-3 bg-[#F0EDE8] dark:bg-[#2A2520]/50 border border-[#E0D8CC] dark:border-[#3A3530]/50 rounded-xl flex items-center justify-between">
                      <p className="text-sm text-[#2A2420]/70 dark:text-[#F0EBE4]/70">
                        Renews on <span className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">{new Date(subscription.current_period_end).toLocaleDateString()}</span>
                      </p>
                      <Button onClick={handleManageBilling} variant="outline" className="text-sm border-[#E0D8CC] dark:border-[#3A3530]">Manage Billing</Button>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Free Plan */}
                    <div className={`border-2 rounded-2xl p-6 transition-all ${
                      currentPlan === 'free' 
                        ? 'border-[#8B9D83] dark:border-[#68D391] bg-[#8B9D83]/10 dark:bg-[#68D391]/10' 
                        : 'border-[#E0D8CC] dark:border-[#2A2520]/50 hover:border-[#8B9D83] dark:hover:border-[#68D391]'
                    }`}>
                      <h3 className="text-lg font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-2">{t('settings.free')}</h3>
                      <p className="text-3xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-4">$0<span className="text-sm font-normal">{t('settings.perMonth')}</span></p>
                      <ul className="space-y-2 mb-6">
                        {PLANS.free.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-[#2A2420]/80 dark:text-[#F0EBE4]/80">
                            <Check className="w-4 h-4 mr-2 text-[#8B9D83] dark:text-[#68D391]" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      {currentPlan === 'free' && (
                        <span className="block text-center py-2 text-[#8B9D83] dark:text-[#68D391] font-medium">
                          {t('settings.currentPlanBadge')}
                        </span>
                      )}
                    </div>

                    {/* Premium Plan */}
                    <div className={`border-2 rounded-2xl p-6 transition-all relative ${
                      currentPlan === 'premium' 
                        ? 'border-purple-500 bg-purple-500/10' 
                        : 'border-[#E0D8CC] dark:border-[#2A2520]/50 hover:border-purple-500'
                    }`}>
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">POPULAR</span>
                      </div>
                      <h3 className="text-lg font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-2 flex items-center">
                        <Crown className="w-5 h-5 mr-2 text-purple-500" />
                        {t('settings.premium')}
                      </h3>
                      <p className="text-3xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-4">${PLANS.premium.price}<span className="text-sm font-normal">{t('settings.perMonth')}</span></p>
                      <ul className="space-y-2 mb-6">
                        {PLANS.premium.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-[#2A2420]/80 dark:text-[#F0EBE4]/80">
                            <Check className="w-4 h-4 mr-2 text-purple-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      {currentPlan === 'premium' ? (
                        <div className="space-y-2">
                          <span className="block text-center py-2 text-purple-500 font-medium">
                            {t('settings.currentPlanBadge')}
                          </span>
                          <Button onClick={handleManageBilling} variant="outline" className="w-full text-sm border-purple-300 text-purple-600 hover:bg-purple-50">
                            Manage Billing
                          </Button>
                        </div>
                      ) : currentPlan === 'free' ? (
                        <Button
                          onClick={() => handleUpgrade('premium')}
                          data-testid="upgrade-premium-button"
                          className="w-full bg-purple-500 hover:bg-purple-600 text-white"
                        >
                          {t('settings.upgradeTo')} {t('settings.premium')}
                        </Button>
                      ) : null}
                    </div>

                    {/* Pro Plan */}
                    <div className={`border-2 rounded-2xl p-6 transition-all ${
                      currentPlan === 'pro' 
                        ? 'border-[#8B7355] bg-[#8B7355]/10' 
                        : 'border-[#E0D8CC] dark:border-[#2A2520]/50 hover:border-[#8B7355]'
                    }`}>
                      <h3 className="text-lg font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-2 flex items-center">
                        <Zap className="w-5 h-5 mr-2 text-[#7C766E]" />
                        {t('settings.pro')}
                      </h3>
                      <p className="text-3xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-4">${PLANS.pro.price}<span className="text-sm font-normal">{t('settings.perMonth')}</span></p>
                      <ul className="space-y-2 mb-6">
                        {PLANS.pro.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-[#2A2420]/80 dark:text-[#F0EBE4]/80">
                            <Check className="w-4 h-4 mr-2 text-[#7C766E]" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      {currentPlan === 'pro' ? (
                        <div className="space-y-2">
                          <span className="block text-center py-2 text-[#7C766E] font-medium">
                            {t('settings.currentPlanBadge')}
                          </span>
                          <Button onClick={handleManageBilling} variant="outline" className="w-full text-sm border-stone-300 text-stone-600 hover:bg-stone-50">
                            Manage Billing
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleUpgrade('pro')}
                          data-testid="upgrade-pro-button"
                          className="w-full bg-[#8B7355] hover:bg-stone-700 text-white"
                        >
                          {t('settings.upgradeTo')} {t('settings.pro')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Coupon Code Section */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-dashed border-purple-300 dark:border-purple-700 rounded-2xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] flex items-center">
                        <CreditCard className="w-5 h-5 mr-2 text-purple-500" />
                        Have a Coupon Code?
                      </h3>
                      <p className="text-sm text-[#2A2420]/70 dark:text-[#F0EBE4]/70 mt-1">
                        Enter your promo code to unlock free access
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                        placeholder="Enter coupon code"
                        disabled={applyingCoupon}
                        className="w-full px-4 py-3 rounded-xl border-2 border-purple-200 dark:border-purple-800 bg-white dark:bg-[#22201C] text-[#2A2420] dark:text-[#F0EBE4] focus:outline-none focus:border-purple-500 font-mono text-lg tracking-wider"
                      />
                    </div>
                    <Button
                      onClick={handleApplyCoupon}
                      disabled={applyingCoupon || !couponCode.trim()}
                      className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {applyingCoupon ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Applying...
                        </>
                      ) : (
                        'Apply'
                      )}
                    </Button>
                  </div>
                  
                  {subscription?.coupon_code && (
                    <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <p className="text-sm text-green-800 dark:text-green-300 flex items-center">
                        <Check className="w-4 h-4 mr-2" />
                        Coupon <span className="font-mono font-bold mx-1">{subscription.coupon_code}</span> applied! 
                        You have <span className="font-bold mx-1">{subscription.plan.toUpperCase()}</span> tier access.
                      </p>
                    </div>
                  )}
                </div>

                {/* Billing History */}
                <div className="bg-white/60 dark:bg-[#22201C]/70 backdrop-blur-sm border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-2xl p-6">
                  <h2 className="text-xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-6">{t('settings.billingHistory')}</h2>
                  
                  {billingHistory.length === 0 ? (
                    <p className="text-[#2A2420]/70 dark:text-[#F0EBE4]/70 text-center py-8">
                      {t('settings.noBillingHistory')}
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#E0D8CC] dark:border-[#2A2520]/50">
                            <th className="text-left py-3 text-[#2A2420] dark:text-[#F0EBE4] font-medium">{t('settings.date')}</th>
                            <th className="text-left py-3 text-[#2A2420] dark:text-[#F0EBE4] font-medium">{t('settings.amount')}</th>
                            <th className="text-left py-3 text-[#2A2420] dark:text-[#F0EBE4] font-medium">{t('settings.status')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {billingHistory.map((item, idx) => (
                            <tr key={idx} className="border-b border-[#E0D8CC]/50 dark:border-[#2A2520]/50/50">
                              <td className="py-3 text-[#2A2420] dark:text-[#F0EBE4]">
                                {new Date(item.date).toLocaleDateString()}
                              </td>
                              <td className="py-3 text-[#2A2420] dark:text-[#F0EBE4]">${item.amount}</td>
                              <td className="py-3">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  item.status === 'paid' 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                                    : item.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                }`}>
                                  {t(`settings.${item.status}`)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="space-y-6">
                {/* Email Notifications */}
                <div className="bg-white/60 dark:bg-[#22201C]/70 backdrop-blur-sm border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-2xl p-6">
                  <h2 className="text-xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-6 flex items-center">
                    <Mail className="w-5 h-5 mr-2" />
                    {t('settings.emailNotifications')}
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-[#E0D8CC] dark:border-[#2A2520]/50">
                      <div>
                        <p className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">{t('settings.emailNotifications')}</p>
                        <p className="text-sm text-[#2A2420]/70 dark:text-[#F0EBE4]/70">{t('settings.emailNotificationsDesc')}</p>
                      </div>
                      <Switch
                        checked={emailNotifications}
                        onCheckedChange={handleEmailNotificationsChange}
                        data-testid="email-notifications-switch"
                      />
                    </div>
                    
                    <div className={`flex items-center justify-between py-3 border-b border-[#E0D8CC] dark:border-[#2A2520]/50 ${!emailNotifications ? 'opacity-70' : ''}`}>
                      <div>
                        <p className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">{t('settings.weeklyDigest')}</p>
                        <p className="text-sm text-[#2A2420]/70 dark:text-[#F0EBE4]/70">{t('settings.weeklyDigestDesc')}</p>
                      </div>
                      <Switch
                        checked={weeklyDigest}
                        onCheckedChange={handleWeeklyDigestChange}
                        disabled={!emailNotifications}
                        data-testid="weekly-digest-switch"
                      />
                    </div>
                    
                    <div className={`flex items-center justify-between py-3 ${!emailNotifications ? 'opacity-70' : ''}`}>
                      <div>
                        <p className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">{t('settings.marketingEmails')}</p>
                        <p className="text-sm text-[#2A2420]/70 dark:text-[#F0EBE4]/70">{t('settings.marketingEmailsDesc')}</p>
                      </div>
                      <Switch
                        checked={marketingEmails}
                        onCheckedChange={handleMarketingEmailsChange}
                        disabled={!emailNotifications}
                        data-testid="marketing-emails-switch"
                      />
                    </div>
                  </div>
                </div>

                {/* Read Aloud Voice */}
                <div className="bg-white/60 dark:bg-[#22201C]/70 backdrop-blur-sm border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-2xl p-6">
                  <h2 className="text-xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-6 flex items-center">
                    <Volume2 className="w-5 h-5 mr-2" />
                    Read aloud voice
                  </h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(SETTINGS_VOICE_OPTIONS).map(([voiceId, voice]) => (
                      <button
                        key={voiceId}
                        type="button"
                        onClick={() => handleVoiceChange(voiceId)}
                        data-testid={`tts-voice-${voiceId}`}
                        className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                          selectedVoice === voiceId
                            ? 'border-[#5C4D6B] bg-[#F0EBE4] text-[#2A2420] dark:border-[#B0A0C0] dark:bg-[#2A2520] dark:text-[#F0EBE4]'
                            : 'border-[#E0D8CC] bg-white/50 text-[#2A2420] hover:bg-[#F8F5F0] dark:border-[#2A2520] dark:bg-[#1F1C19]/50 dark:text-[#F0EBE4] dark:hover:bg-[#2A2520]/70'
                        }`}
                      >
                        <span className="block font-medium">{voice.name}</span>
                        <span className="block text-sm opacity-70 mt-1">{voice.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Appearance */}
                <div className="bg-white/60 dark:bg-[#22201C]/70 backdrop-blur-sm border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-2xl p-6">
                  <h2 className="text-xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-6 flex items-center">
                    {darkMode ? <Moon className="w-5 h-5 mr-2" /> : <Sun className="w-5 h-5 mr-2" />}
                    {t('settings.theme')}
                  </h2>
                  
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">{t('settings.themeDesc')}</p>
                      <p className="text-sm text-[#2A2420]/70 dark:text-[#F0EBE4]/70">
                        {darkMode ? t('settings.dark') : t('settings.light')}
                      </p>
                    </div>
                    <DarkModeToggle />
                  </div>
                </div>

                {/* Language */}
                <div className="bg-white/60 dark:bg-[#22201C]/70 backdrop-blur-sm border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-2xl p-6">
                  <h2 className="text-xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-6 flex items-center">
                    <Globe className="w-5 h-5 mr-2" />
                    {t('settings.language')}
                  </h2>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => handleLanguageChange(lang.code)}
                        data-testid={`language-option-${lang.code}`}
                        className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                          selectedLanguage === lang.code
                            ? 'border-[#5C4D6B] bg-[#F0EBE4] text-[#2A2420] dark:border-[#B0A0C0] dark:bg-[#2A2520] dark:text-[#F0EBE4]'
                            : 'border-[#E0D8CC] bg-white/50 text-[#2A2420] hover:bg-[#F8F5F0] dark:border-[#2A2520] dark:bg-[#1F1C19]/50 dark:text-[#F0EBE4] dark:hover:bg-[#2A2520]/70'
                        }`}
                      >
                        <span className="text-xl block mb-1">{lang.flag}</span>
                        <span className="block font-medium text-sm">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Account Tab */}
            {activeTab === 'account' && (
              <div className="space-y-6">
                {/* Logout */}
                <div className="bg-white/60 dark:bg-[#22201C]/70 backdrop-blur-sm border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-2xl p-6">
                  <h2 className="text-xl font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-6">{t('settings.logout')}</h2>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">{t('settings.logoutDesc')}</p>
                    </div>
                    <Button
                      onClick={handleLogout}
                      variant="outline"
                      data-testid="logout-button"
                      className="dark:border-[#2A2520] dark:text-[#F0EBE4] dark:hover:bg-[#2A2520]/50"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('settings.logout')}
                    </Button>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50 rounded-2xl p-6">
                  <h2 className="text-xl font-bold text-red-700 dark:text-red-400 mb-6 flex items-center">
                    <Trash2 className="w-5 h-5 mr-2" />
                    {t('settings.dangerZone')}
                  </h2>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-red-700 dark:text-red-400">{t('settings.deleteAccount')}</p>
                      <p className="text-sm text-red-600/70 dark:text-red-400/70">{t('settings.deleteAccountDesc')}</p>
                    </div>
                    <Button
                      variant="destructive"
                      data-testid="delete-account-button"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('settings.deleteAccount')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </AppShell>
  );
}

export default SettingsPage;
